import { Spin } from "antd";
import { useState, type KeyboardEvent } from "react";
import styles from "./RecommendCard.module.css";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  MinusOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { useQueryState } from "nuqs";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../../components/CustomAntdComponents/Button";
import { useFinancialAnalytics } from "../../../../../../api/services/financial-integration.hooks";
import { AddToWatchlistModal } from "./AddToWatchlistModal";
import { useAuth } from "../../../../../../api/auth/useAuth";
import { Tag } from "../../../../../components/Tag/Tag";
import { normalizeHalalStatus } from "./ShariahReport/shariahReportHalalStatus";

const DEFAULT_LOGO = "/city.webp";

/** Query `tab` value for Shariah Report (see TopHalalStockInfo tab keys). */
const SHARIAH_REPORT_TAB_KEY = "4";

const parseNumericValue = (value?: string | number | null): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string") {
    const sanitized = value.replace(/[^0-9.+-]+/g, "");
    if (!sanitized) {
      return null;
    }
    const parsed = Number.parseFloat(sanitized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const formatCurrency = (
  value: number | null,
  currency: string,
  fallback?: string
): string => {
  if (value === null) {
    return fallback ?? "-";
  }

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return fallback ?? value.toString();
  }
};

const formatSignedCurrency = (
  value: number | null,
  currency: string,
  fallback?: string
): string => {
  if (value === null) {
    return fallback ?? "-";
  }

  const absoluteFormatted = formatCurrency(Math.abs(value), currency, fallback);
  if (value > 0) {
    return `+${absoluteFormatted}`;
  }

  if (value < 0) {
    return `-${absoluteFormatted}`;
  }

  return absoluteFormatted;
};

const formatPercentage = (value?: string | number | null): string | null => {
  const numeric = parseNumericValue(value);

  if (numeric === null) {
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
    return null;
  }

  const formatted = `${
    numeric > 0 ? "+" : numeric < 0 ? "" : ""
  }${numeric?.toFixed(2)}%`;
  return formatted;
};

const joinClasses = (...classNames: Array<string | false | undefined>) =>
  classNames.filter(Boolean).join(" ");

const getHalalStatusTagColor = (
  halalStatus?: string | null
): string | undefined => {
  if (!halalStatus) {
    return undefined;
  }

  const normalizedStatus = halalStatus.toLowerCase().trim();

  if (normalizedStatus === "halal") {
    return "green";
  }

  if (normalizedStatus === "doubtful") {
    return "yellow";
  }

  if (normalizedStatus === "not-halal" || normalizedStatus === "not_halal") {
    return "error";
  }

  if (normalizedStatus === "unqualified" || normalizedStatus === "unrated") {
    return "default";
  }

  return undefined;
};

export const RecommendCard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { symbol = "" } = useParams<{ symbol: string }>();
  const [, setActiveTab] = useQueryState("tab", {
    defaultValue: "1",
    parse: (value) => value || "1",
    serialize: (value) => value,
  });
  const [isAddToWatchlistModalOpen, setIsAddToWatchlistModalOpen] =
    useState(false);
  const { data, isLoading, error } = useFinancialAnalytics(
    { symbol },
    { enabled: Boolean(symbol) }
  );

  const analytics = data?.data;
  const companyProfile = analytics?.companyProfile;
  const currency = companyProfile?.currency || "USD";
  const priceNumber = parseNumericValue(companyProfile?.price);
  const priceDisplay = formatCurrency(
    priceNumber,
    currency,
    companyProfile?.price
  );
  const changeNumber = parseNumericValue(companyProfile?.change);
  const changePercentageNumber =
    parseNumericValue(companyProfile?.changePercentage) ??
    parseNumericValue(analytics?.changePercentage);
  const changePercentageDisplay =
    formatPercentage(companyProfile?.changePercentage) ??
    formatPercentage(analytics?.changePercentage) ??
    "-";
  const changeDisplay = formatSignedCurrency(
    changeNumber,
    currency,
    companyProfile?.change
  );
  const isPositiveChange =
    typeof changePercentageNumber === "number"
      ? changePercentageNumber > 0
      : typeof changeNumber === "number"
      ? changeNumber > 0
      : null;

  const statsIcon =
    isPositiveChange === null
      ? MinusOutlined
      : isPositiveChange
      ? ArrowUpOutlined
      : ArrowDownOutlined;

  const statsClassName =
    isPositiveChange === null
      ? styles.statsNeutral
      : isPositiveChange
      ? styles.statsPositive
      : styles.statsNegative;

  const halalStatus = analytics?.halal_status;

  const openShariahReportTab = () => {
    void setActiveTab(SHARIAH_REPORT_TAB_KEY);
  };

  const onHalalStatusKeyDown = (e: KeyboardEvent<HTMLSpanElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openShariahReportTab();
    }
  };
  const overtitleParts = [
    companyProfile?.exchange,
    analytics?.symbol ?? symbol?.toUpperCase(),
    companyProfile?.country ? `(${companyProfile.country})` : null,
  ]
    .filter(Boolean)
    .join(" ");

  const overtitle =
    overtitleParts.trim().length > 0
      ? overtitleParts
      : symbol
      ? symbol.toUpperCase()
      : undefined;

  const timeLabel =
    companyProfile?.exchangeFullName ||
    companyProfile?.exchange ||
    companyProfile?.currency;

  const ChangeIcon = statsIcon;

  const logoSource =
    analytics?.logoURL || companyProfile?.image || companyProfile?.defaultImage;

  return (
    <div className={styles.card}>
      <div className={styles.logo}>
        {isLoading ? (
          <div className={styles.loader}>
            <Spin size="small" />
          </div>
        ) : (
          <img
            src={logoSource || DEFAULT_LOGO}
            alt={companyProfile?.companyName ?? analytics?.symbol ?? symbol}
            onError={(event) => {
              const target = event.currentTarget;
              if (target.src !== DEFAULT_LOGO) {
                target.src = DEFAULT_LOGO;
              }
            }}
          />
        )}
      </div>

      <div className={styles.info}>
        {isLoading ? (
          <div className={styles.loader}>
            <Spin />
          </div>
        ) : error ? (
          <div className={styles.errorMessage}>
            {t("pages.topHalal.info.recommendCard.failedLoad")}
          </div>
        ) : analytics ? (
          <>
            <p className={styles.overtitle}>{overtitle ?? "—"}</p>
            <div className={styles.titleRow}>
              {/* "Apple Inc. (AAPL)": what people search for; the status tag
                  sits next to the heading, not inside it. */}
              <h1 className={styles.title}>
                {companyProfile?.companyName
                  ? `${companyProfile.companyName} (${analytics.symbol.toUpperCase()})`
                  : analytics.symbol.toUpperCase()}
              </h1>
              {halalStatus ? (
                <Tag
                  className={styles.halalStatusTag}
                  color={getHalalStatusTagColor(halalStatus)}
                  role="button"
                  tabIndex={0}
                  aria-label={t(
                    "pages.topHalal.info.recommendCard.openShariahReport",
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    openShariahReportTab();
                  }}
                  onKeyDown={onHalalStatusKeyDown}
                >
                  {(() => {
                    const normalized = normalizeHalalStatus(halalStatus);
                    if (normalized === "halal") {
                      return t("pages.topHalal.info.shariahReport.halal");
                    }
                    if (normalized === "doubtful") {
                      return t("pages.topHalal.info.shariahReport.doubtful");
                    }
                    if (normalized === "not_halal") {
                      return t("pages.topHalal.info.shariahReport.notHalal");
                    }
                    if (normalized === "unqualified") {
                      return t("pages.topHalal.info.shariahReport.notQualified");
                    }
                    return halalStatus
                      .replace(/-/g, " ")
                      .replace(/\b\w/g, (char) => char.toUpperCase());
                  })()}
                </Tag>
              ) : null}
            </div>
            <div className={styles.subtitle}>
              <h3 className={styles.price}>{priceDisplay}</h3>
              <div className={joinClasses(styles.stats, statsClassName)}>
                <ChangeIcon />
                {changeDisplay}
                {changePercentageDisplay ? ` (${changePercentageDisplay})` : ""}
              </div>
              {timeLabel ? (
                <div className={styles.time}>{timeLabel}</div>
              ) : null}
            </div>
          </>
        ) : (
          <div className={styles.statusMessage}>
            {t("pages.topHalal.info.recommendCard.noData")}
          </div>
        )}
      </div>

      {user && (
        <Button
          className={styles.btn}
          type="primary"
          size="large"
          icon={<PlusOutlined />}
          disabled={isLoading || !!error || !symbol}
          onClick={() => setIsAddToWatchlistModalOpen(true)}
        >
          {t("pages.topHalal.addToPortfolio")}
        </Button>
      )}
      <AddToWatchlistModal
        open={isAddToWatchlistModalOpen}
        onCancel={() => setIsAddToWatchlistModalOpen(false)}
        symbol={symbol}
      />
    </div>
  );
};
